import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import { TypesenseCollectionConfig } from '@/api/typesense/types';
import { BaseSelectOption } from '@/forms/types';

/**
 * Manages TanStack Query cache updates for Typesense-backed `SelectInputField` components
 * when a new option is created. Use in mutation `onSuccess` callbacks to make the new option
 * appear immediately without waiting for Typesense to index it.
 *
 * See `src/hooks/forms/README.md` for full documentation.
 */
export const useUpdateSelectQueryDataOnOptionCreation = () => {
  const queryClient = useQueryClient();

  /**
   * Optimistically injects a newly created option into the base (no search input) infinite
   * query cache for the given Typesense collection. Only targets queries where `searchInput === ''`
   * to avoid bleeding the option into search-filtered caches where it may not match.
   */
  const injectOptionIntoBaseQuery = (
    typesenseCollection: TypesenseCollectionConfig,
    newOption: BaseSelectOption,
  ) => {
    queryClient.setQueriesData(
      {
        predicate: (query) => {
          const key = query.queryKey as unknown[];
          return (
            // needs to align with SelectInputField.tsx currentQueryKey construction
            key[0] === typesenseCollection.name &&
            key[1] === typesenseCollection.keyName &&
            key[3] === '' // base query only — no search input
          );
        },
      },
      (oldData: InfiniteData<BaseSelectOption[] | undefined> | undefined) => {
        if (!oldData) return { pages: [[newOption]], pageParams: [1] };

        return {
          pages: [...oldData.pages, [newOption]],
          pageParams: [...oldData.pageParams, oldData.pageParams.length + 1],
        };
      },
    );
  };

  /**
   * Invalidates all search-filtered (non-base) query caches for the given Typesense collection,
   * so they refetch fresh results from Typesense on next use. Call alongside `injectOptionIntoBaseQuery`
   * to ensure any previously cached search results are cleared after a new option is created.
   */
  const invalidateSearchQueries = (
    typesenseCollection: TypesenseCollectionConfig,
  ) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey as unknown[];
        return (
          // needs to align with SelectInputField.tsx currentQueryKey construction
          key[0] === typesenseCollection.name &&
          key[1] === typesenseCollection.keyName &&
          key[3] !== '' // only filtered/search queries
        );
      },
    });
  };

  return { injectOptionIntoBaseQuery, invalidateSearchQueries };
};
